import { http } from './request'

/** 一级类型（信息发布） */
export type InformationPrimaryType = '领事服务' | '宣传视频' | '平安课堂'

/** 前端展示等级（高/中/低），后端字段为 infoLevel: NORMAL/IMPORTANT/URGENT */
export type InformationAlertLevel = 'high' | 'medium' | 'low'

/** 类型 → 可选二级分类（空数组表示无分类） */
export const INFORMATION_TYPE_CATEGORY_MAP: Record<InformationPrimaryType, string[]> = {
  领事服务: [],
  宣传视频: ['旅行安全', '应急处理', '法律知识'],
  平安课堂: [],
}

export const INFORMATION_PRIMARY_TYPES: InformationPrimaryType[] = [
  '领事服务',
  '宣传视频',
  '平安课堂',
]

export const informationLevelLabel: Record<InformationAlertLevel, string> = {
  high: '高',
  medium: '中',
  low: '低',
}

export interface InformationReleaseListQuery {
  /** 一级类型，不传或 all 为全部 */
  primaryType?: InformationPrimaryType | 'all'
  /** 标题模糊 */
  title?: string
  /** 发布状态（草稿/已发布/已下架），不传或 all 为全部 */
  status?: 'draft' | 'published' | 'offline' | 'all'
  /** 发布时间开始（yyyy-MM-dd 或 yyyy-MM-dd HH:mm:ss） */
  publishTimeStart?: string
  /** 发布时间结束（yyyy-MM-dd 或 yyyy-MM-dd HH:mm:ss） */
  publishTimeEnd?: string
  page?: number
  pageSize?: number
}

export interface InformationReleaseRecord {
  id: string
  title: string
  primaryType: InformationPrimaryType
  /** 无二级分类时为空字符串 */
  secondaryType: string
  level: InformationAlertLevel
  contentHtml: string
  status: 'draft' | 'published' | 'offline'
  /** 已发布时 ISO 字符串；草稿为 null */
  publishedAt: string | null
  updatedAt: string
  /** 创建人名称（后端 CommonEntity 转译字段） */
  createUserName?: string
}

export interface InformationReleasePayload {
  title: string
  primaryType: InformationPrimaryType
  secondaryType: string
  level: InformationAlertLevel
  contentHtml: string
}

export interface InformationReleaseListResult {
  list: InformationReleaseRecord[]
  total: number
}

type InformationBackendStatus = 'DRAFT' | 'PUBLISHED' | 'OFFLINE' | string
type InformationBackendInfoLevel = 'NORMAL' | 'IMPORTANT' | 'URGENT' | string

interface InformationBackendRecord {
  id?: string
  title?: string
  content?: string
  primaryType?: string
  secondaryType?: string
  infoLevel?: InformationBackendInfoLevel
  status?: InformationBackendStatus
  publishTime?: string | null
  updateTime?: string
  createTime?: string
  createUserName?: string
}

interface MybatisPage<T> {
  records?: T[]
  total?: number
  current?: number
  size?: number
}

function toFrontendStatus(s: unknown): 'draft' | 'published' | 'offline' {
  const v = String(s ?? '')
    .trim()
    .toUpperCase()
  if (v === 'PUBLISHED') return 'published'
  if (v === 'OFFLINE') return 'offline'
  return 'draft'
}

function toBackendStatus(s: 'draft' | 'published' | 'offline'): 'DRAFT' | 'PUBLISHED' | 'OFFLINE' {
  if (s === 'published') return 'PUBLISHED'
  if (s === 'offline') return 'OFFLINE'
  return 'DRAFT'
}

function toFrontendLevel(v: unknown): InformationAlertLevel {
  const s = String(v ?? '')
    .trim()
    .toUpperCase()
  if (s === 'URGENT') return 'high'
  if (s === 'IMPORTANT') return 'medium'
  return 'low'
}

function toBackendInfoLevel(level: InformationAlertLevel): 'NORMAL' | 'IMPORTANT' | 'URGENT' {
  if (level === 'high') return 'URGENT'
  if (level === 'medium') return 'IMPORTANT'
  return 'NORMAL'
}

function isoOrNull(input: unknown): string | null {
  const s = String(input ?? '').trim()
  return s ? s : null
}

function mapBackendRow(rec: InformationBackendRecord): InformationReleaseRecord | null {
  const id = String(rec.id ?? '').trim()
  if (!id) return null
  const primary = String(rec.primaryType ?? '').trim() as InformationPrimaryType
  return {
    id,
    title: String(rec.title ?? '').trim(),
    primaryType: (INFORMATION_PRIMARY_TYPES.includes(primary)
      ? primary
      : '领事服务') as InformationPrimaryType,
    secondaryType: String(rec.secondaryType ?? '').trim(),
    level: toFrontendLevel(rec.infoLevel),
    contentHtml: String(rec.content ?? '').trim() || '<p></p>',
    status: toFrontendStatus(rec.status),
    publishedAt: isoOrNull(rec.publishTime),
    updatedAt: String(rec.updateTime ?? rec.createTime ?? '')
      .trim()
      .slice(0, 10),
    createUserName: rec.createUserName ? String(rec.createUserName).trim() : undefined,
  }
}

/**
 * GET /biz/overseas/information/page
 */
export async function fetchInformationReleaseList(
  query: InformationReleaseListQuery = {},
): Promise<InformationReleaseListResult> {
  const current = query.page ?? 1
  const size = query.pageSize ?? 10
  const status = query.status && query.status !== 'all' ? toBackendStatus(query.status) : undefined
  const primaryType =
    query.primaryType && query.primaryType !== 'all' ? query.primaryType : undefined
  const raw = await http.get<MybatisPage<InformationBackendRecord>>(
    '/biz/overseas/information/page',
    {
      params: {
        current,
        size,
        status,
        primaryType,
        title: query.title?.trim() || undefined,
        publishTimeStart: query.publishTimeStart?.trim() || undefined,
        publishTimeEnd: query.publishTimeEnd?.trim() || undefined,
      } as Record<string, string | number | undefined>,
    },
  )
  const records = Array.isArray(raw?.records) ? raw.records : []
  const list = records.map(mapBackendRow).filter((x): x is InformationReleaseRecord => x != null)
  const total = typeof raw?.total === 'number' ? raw.total : list.length
  return { list, total }
}

/**
 * GET /biz/overseas/information/manageDetail?id=
 */
export async function fetchInformationReleaseDetail(id: string): Promise<InformationReleaseRecord> {
  const sid = id?.trim()
  if (!sid) throw new Error('id 不能为空')
  const raw = await http.get<InformationBackendRecord>('/biz/overseas/information/manageDetail', {
    params: { id: sid },
  })
  const mapped = mapBackendRow(raw)
  if (!mapped) throw new Error('记录不存在')
  return mapped
}

/**
 * POST /biz/overseas/information/add（保存草稿）
 */
export async function createInformationRelease(payload: InformationReleasePayload): Promise<void> {
  await http.post<void>('/biz/overseas/information/add', {
    title: payload.title,
    content: payload.contentHtml,
    primaryType: payload.primaryType,
    secondaryType: payload.secondaryType,
    infoLevel: toBackendInfoLevel(payload.level),
    status: 'DRAFT',
  })
}

/**
 * POST /biz/overseas/information/edit（保存草稿）
 */
export async function updateInformationRelease(
  id: string,
  payload: InformationReleasePayload,
): Promise<void> {
  const sid = id?.trim()
  if (!sid) throw new Error('id 不能为空')
  await http.post<void>('/biz/overseas/information/edit', {
    id: sid,
    title: payload.title,
    content: payload.contentHtml,
    primaryType: payload.primaryType,
    secondaryType: payload.secondaryType,
    infoLevel: toBackendInfoLevel(payload.level),
    status: 'DRAFT',
  })
}

/**
 * POST /biz/overseas/information/delete（逻辑删除）
 */
export async function deleteInformationRelease(id: string): Promise<void> {
  const sid = id?.trim()
  if (!sid) return
  await http.post<void>('/biz/overseas/information/delete', { id: sid })
}

/**
 * POST /biz/overseas/information/publish?id=
 */
export async function publishInformationRelease(id: string): Promise<void> {
  const sid = id?.trim()
  if (!sid) throw new Error('id 不能为空')
  await http.post<void>('/biz/overseas/information/publish', undefined, { params: { id: sid } })
}

/**
 * POST /biz/overseas/information/offline?id=
 */
export async function offlineInformationRelease(id: string): Promise<void> {
  const sid = id?.trim()
  if (!sid) return
  await http.post<void>('/biz/overseas/information/offline', undefined, { params: { id: sid } })
}
